import { useState } from 'react';
import { ErreurApi } from '../api/client';
import { useMajExpedition } from '../api/hooks';
import type { CommandeDetaillee } from '../api/types';
import { useToasts } from './Toasts';

/**
 * « Le fournisseur ne retrouve pas la commande » — le constat, posé à la main.
 *
 * L'incident `COMMANDE_MANQUANTE` existait de bout en bout : le DTO l'acceptait,
 * le service l'écrivait, le moteur levait une action rouge « Commande
 * introuvable chez X », et deux écrans savaient la traiter en proposant
 * « Repasser la commande ». Mais **aucun écran ne l'envoyait jamais** : zéro
 * commande sur quatre-vingt-dix le portait, et le seul producteur vivant de
 * cette action était le relevé du portail Fortemix. Un fournisseur qui répond au
 * téléphone « nous n'avons jamais reçu cette commande » ne pouvait donc pas être
 * consigné — sauf s'il s'appelait FORTEMIX.
 *
 * Ce bouton ferme le circuit. Il vit à côté du bouton de relance, parce que
 * c'est en relançant qu'on l'apprend : on écrit, on téléphone, et la réponse est
 * « on ne l'a pas ». Réclamer une date d'expédition n'y changerait plus rien —
 * ce qu'il faut, c'est repasser la commande, et c'est exactement ce que
 * l'action rouge proposera ensuite.
 *
 * Le constat se lève tout seul : poser une date d'expédition clôt l'incident.
 */
export function CommandeIntrouvable({
  commande, enAttente,
}: {
  commande: CommandeDetaillee;
  enAttente?: boolean;
}) {
  const { ajouter } = useToasts();
  const majExpedition = useMajExpedition();
  const [confirme, setConfirme] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const declarer = async () => {
    setErreur(null);
    try {
      await majExpedition.mutateAsync({ id: commande.id, corps: { commandeManquante: true } });
      ajouter(`${commande.ref} : le fournisseur ne retrouve pas la commande — à repasser.`, 'bad');
      setConfirme(false);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "L'enregistrement a échoué.");
    }
  };

  // Déjà constaté : le dire plutôt que d'offrir de le redire. L'action rouge
  // porte désormais la suite, il n'y a plus rien à déclarer ici.
  if (commande.incident === 'COMMANDE_MANQUANTE') return null;

  if (!confirme) {
    return (
      <button
        type="button" className="btn sm" disabled={enAttente || majExpedition.isPending}
        onClick={() => { setErreur(null); setConfirme(true); }}
        title="Le fournisseur répond qu'il n'a jamais reçu cette commande"
      >
        Commande introuvable chez eux
      </button>
    );
  }

  return (
    <div style={{ flexBasis: '100%', marginTop: 8 }}>
      <p style={{ fontSize: 12.5, marginBottom: 8 }}>
        Le fournisseur confirme qu&apos;il n&apos;a <strong>jamais reçu</strong> cette commande ?
        Elle cessera de réclamer une date d&apos;expédition et passera en rouge, à repasser.
        Saisir une date d&apos;expédition lèvera le constat.
      </p>
      {erreur && <p className="err" role="alert">{erreur}</p>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button" className="btn pri sm" disabled={majExpedition.isPending}
          onClick={() => void declarer()}
        >
          {majExpedition.isPending ? <><span className="spin" /> Enregistrement…</> : 'Oui, il ne l’a pas'}
        </button>
        <button type="button" className="btn sm" onClick={() => setConfirme(false)}>
          Annuler
        </button>
      </div>
    </div>
  );
}
